import { useCallback, useEffect, useRef, useState } from 'react';

/** Horizontal drag, in pixels, required to commit a swipe instead of springing back. */
const COMMIT_THRESHOLD = 75;

/** Total movement below which a pointer interaction counts as a tap, not a drag. */
const TAP_SLOP = 10;

/** How long the fly-off animation runs before the card is removed. */
const FLY_DURATION_MS = 360;

/**
 * Pointer handling for a draggable card.
 *
 * Move and release are bound to `document` rather than the card, because a fast
 * drag routinely leaves the element's bounds and would otherwise strand the
 * card mid-gesture.
 *
 * @param {object} options
 * @param {boolean} options.enabled  Only the top card of the stack should drag.
 * @param {(direction: 'left' | 'right') => void} options.onCommit
 * @param {() => void} [options.onTap]
 * @returns {{
 *   offsetX: number,
 *   flyDirection: 'left' | 'right' | null,
 *   isDragging: boolean,
 *   fly: (direction: 'left' | 'right') => void,
 *   handlers: {onMouseDown: Function, onTouchStart: Function, onClick: Function},
 * }}
 */
export function useSwipeGesture({ enabled, onCommit, onTap }) {
  const [offsetX, setOffsetX] = useState(0);
  const [flyDirection, setFlyDirection] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const dragging = useRef(false);
  const start = useRef({ x: 0, y: 0 });
  const delta = useRef({ x: 0, y: 0 });

  /** Animates the card off-screen, then reports the swipe. */
  const fly = useCallback(
    (direction) => {
      setFlyDirection(direction);
      window.setTimeout(() => onCommit(direction), FLY_DURATION_MS);
    },
    [onCommit]
  );

  useEffect(() => {
    if (!enabled) return;

    const onMove = (event) => {
      if (!dragging.current) return;
      const point = event.touches?.[0] ?? event;
      delta.current = { x: point.clientX - start.current.x, y: point.clientY - start.current.y };
      setOffsetX(delta.current.x);
    };

    const onRelease = () => {
      if (!dragging.current) return;
      dragging.current = false;
      setIsDragging(false);

      if (Math.abs(delta.current.x) > COMMIT_THRESHOLD) {
        fly(delta.current.x > 0 ? 'right' : 'left');
      } else {
        setOffsetX(0);
      }
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onRelease);
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend', onRelease);

    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onRelease);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onRelease);
    };
  }, [enabled, fly]);

  const onPointerDown = (event) => {
    if (!enabled || flyDirection) return;
    const point = event.touches?.[0] ?? event;
    dragging.current = true;
    delta.current = { x: 0, y: 0 };
    start.current = { x: point.clientX, y: point.clientY };
    setIsDragging(true);
    // Suppress the browser's native image drag for mouse input. Touch scrolling
    // is already controlled by `touch-action: none`; calling preventDefault
    // from React's passive touch listener logs a browser warning.
    if (!event.touches && event.cancelable) event.preventDefault();
  };

  const onClick = () => {
    if (!enabled || flyDirection || !onTap) return;
    const moved = Math.abs(delta.current.x) > TAP_SLOP || Math.abs(delta.current.y) > TAP_SLOP;
    if (!moved) onTap();
  };

  return {
    offsetX,
    flyDirection,
    isDragging,
    fly,
    handlers: { onMouseDown: onPointerDown, onTouchStart: onPointerDown, onClick },
  };
}
